import React, { useState, useEffect } from 'react';
import './RegistrationForm.css';
import { registerVisitor, getPrivacyConfig } from '../../services/api';

function RegistrationForm({ onSuccess }) {
  const [formData, setFormData] = useState({
    name: '',
    company: '',
    reason: '',
    phone: ''
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);
  const [fieldErrors, setFieldErrors] = useState({
    name: '',
    company: '',
    reason: '',
    phone: ''
  });
  const [privacyEmail, setPrivacyEmail] = useState('');

  useEffect(() => {
    getPrivacyConfig().then(config => setPrivacyEmail(config.contact_email)).catch(() => {});
  }, []);

  // Sanitize input before setting state
  const sanitizeInput = (value) => {
    return value
      .replace(/<[^>]*>/g, '') // Remove HTML tags
      .replace(/[<>\"\']/g, '') // Remove dangerous characters
      .replace(/\s+/g, ' '); // Normalize multiple spaces to single space
  };

  // Validation functions
  const validateName = (name) => {
    if (!name) return 'Namn är obligatoriskt';
    if (name.length < 2 || name.length > 100) {
      return 'Namn måste vara mellan 2-100 tecken';
    }
    // Allow Swedish and international letters, spaces, hyphens, apostrophes
    if (!/^[a-zA-ZåäöÅÄÖ\s\-\.']+$/.test(name)) {
      return 'Namn får inte innehålla siffror eller specialtecken';
    }
    return '';
  };

  const validatePhone = (phone) => {
    if (!phone) return 'Telefonnummer är obligatoriskt';
    const cleaned = phone.replace(/[\s\-\(\)]+/g, '');
    // International format
    if (!/^\+?\d{7,15}$/.test(cleaned)) {
      return 'Ogiltigt telefonnummer (internationellt format accepterat)';
    }
    return '';
  };

  const validateCompany = (company) => {
    if (!company) return 'Företag är obligatoriskt';
    if (company.length < 2 || company.length > 255) {
      return 'Företag måste vara mellan 2-255 tecken';
    }
    return '';
  };

  const validateReason = (reason) => {
    if (!reason) return 'Ärende är obligatoriskt';
    if (reason.length < 2 || reason.length > 255) {
      return 'Ärende måste vara mellan 2-255 tecken';
    }
    return '';
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    const sanitizedValue = sanitizeInput(value);
    
    setFormData(prev => ({
      ...prev,
      [name]: sanitizedValue
    }));

    // Clear field error when user starts typing
    if (fieldErrors[name]) {
      setFieldErrors(prev => ({
        ...prev,
        [name]: ''
      }));
    }
  };


  const handlePaste = (e, field) => {
    e.preventDefault();
    const text = e.clipboardData.getData('text/plain');
    const sanitized = sanitizeInput(text);
    setFormData(prev => ({
      ...prev,
      [field]: sanitized
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    // Trim all fields before validation
    const trimmedData = {
      name: formData.name.trim(),
      company: formData.company.trim(),
      reason: formData.reason.trim(),
      phone: formData.phone.trim()
    };

    // Validate all fields
    const errors = {
      name: validateName(trimmedData.name),
      company: validateCompany(trimmedData.company),
      reason: validateReason(trimmedData.reason),
      phone: validatePhone(trimmedData.phone)
    };

    setFieldErrors(errors);

    // Check if any errors exist
    const hasErrors = Object.values(errors).some(error => error !== '');
    if (hasErrors) {
      setError('Vänligen korrigera felen nedan');
      return;
    }

    setLoading(true);
    setError('');

    try {
      await registerVisitor(trimmedData);
      setSuccess(true);
      
      // Clear form
      setFormData({
        name: '',
        company: '',
        reason: '',
        phone: ''
      });
      setFieldErrors({
        name: '',
        company: '',
        reason: '',
        phone: ''
      });
      
      // Redirect to current visitors after 2 seconds
      setTimeout(() => {
        onSuccess();
      }, 2000);
    } catch (err) {
      // Check if it's a validation error from backend
      if (err.response && err.response.data && err.response.data.detail) {
        if (Array.isArray(err.response.data.detail)) {
          // Handle Pydantic validation errors
          const backendErrors = {};
          err.response.data.detail.forEach(error => {
            if (error.loc && error.loc[1]) {
              backendErrors[error.loc[1]] = error.msg;
            }
          });
          setFieldErrors(prev => ({ ...prev, ...backendErrors }));
          setError('Vänligen korrigera felen nedan');
        } else {
          setError(err.response.data.detail);
        }
      } else {
        setError('Något gick fel vid registreringen. Försök igen.');
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="registration-container">
      <h2>Registrera ny besökare</h2>

      {success && (
        <div className="success-message">
          Besökaren har registrerats! Omdirigerar till nuvarande gäster...
        </div>
      )}

      {error && (
        <div className="error-message">
          {error}
        </div>
      )}

      <form onSubmit={handleSubmit} className="registration-form">
        <div className="form-group">
          <label htmlFor="name">Namn *</label>
          <input
            type="text"
            id="name"
            name="name"
            value={formData.name}
            onChange={handleChange}
            onPaste={(e) => handlePaste(e, 'name')}
            placeholder="För- och efternamn"
            disabled={loading}
            autoComplete="name"
            maxLength={100}
            className={fieldErrors.name ? 'error' : ''}
            aria-invalid={fieldErrors.name ? 'true' : 'false'}
          />
          {fieldErrors.name && <span className="error-text">{fieldErrors.name}</span>}
        </div>

        <div className="form-group">
          <label htmlFor="company">Företag *</label>
          <input
            type="text"
            id="company"
            name="company"
            value={formData.company}
            onChange={handleChange}
            onPaste={(e) => handlePaste(e, 'company')}
            placeholder="Företagsnamn"
            disabled={loading}
            autoComplete="organization"
            maxLength={255}
            className={fieldErrors.company ? 'error' : ''}
            aria-invalid={fieldErrors.company ? 'true' : 'false'}
          />
          {fieldErrors.company && <span className="error-text">{fieldErrors.company}</span>}
        </div>

        <div className="form-group">
          <label htmlFor="reason">Ärende *</label>
          <input
            type="text"
            id="reason"
            name="reason"
            value={formData.reason}
            onChange={handleChange}
            onPaste={(e) => handlePaste(e, 'reason')}
            placeholder="T.ex. Möte, Leverans, Intervju"
            disabled={loading}
            maxLength={255}
            className={fieldErrors.reason ? 'error' : ''}
            aria-invalid={fieldErrors.reason ? 'true' : 'false'}
          />
          {fieldErrors.reason && <span className="error-text">{fieldErrors.reason}</span>}
        </div>

        <div className="form-group">
          <label htmlFor="phone">Telefonnummer *</label>
          <input
            type="tel"
            id="phone"
            name="phone"
            value={formData.phone}
            onChange={handleChange}
            onPaste={(e) => handlePaste(e, 'phone')}
            placeholder="+46 70 123 45 67"
            disabled={loading}
            autoComplete="tel"
            maxLength={50}
            className={fieldErrors.phone ? 'error' : ''}
            aria-invalid={fieldErrors.phone ? 'true' : 'false'}
          />
          {fieldErrors.phone && <span className="error-text">{fieldErrors.phone}</span>}
        </div>

        <button
          type="submit"
          className="submit-button"
          disabled={loading}
        >
          {loading ? 'Registrerar...' : 'Registrera'}
        </button>
      </form>

      <div className="privacy-notice">
        <p>
          Genom att registrera dig samtycker du till att dina personuppgifter (namn, företag, telefonnummer och besöksärende)
          lagras i vårt system för säkerhet och spårbarhet. Informationen raderas automatiskt efter 30 dagar.
        </p>
        <p>
          Personuppgiftsansvarig: Lantmännen<br />
          Kontakt för dataskyddsförfrågningar: {privacyEmail}<br />
          Du har rätt att få tillgång till, rätta eller radera dina uppgifter.
        </p>
      </div>
    </div>
  );
}

export default RegistrationForm;